import type { Failure } from "@/modules/shared/failure";
import { renderFieldErrors } from "@/modules/shared/fieldCopy";
import { SYSTEM_ERROR, type FormState } from "@/modules/shared/forms";

/**
 * The **web's** copy for every code an auth core can refuse with, and the one
 * place a `Failure` becomes a `FormState` (R22 T6).
 *
 * Lifted out of `./auth.ts` because that file is `"use server"`, where every
 * export must be an async function, so these could neither be exported nor
 * unit-tested there. Every sentence is unchanged from before the cores existed.
 *
 * Copy and behavior are specified in `docs/ux/screens/`
 * {SignUpScreen, PhoneVerificationScreen, LogInScreen, StaffLogInScreen,
 * AdminLogInScreen, JoinAccountScreen}`.md`.
 */

/**
 * The resend confirmation, **deliberately generic** (H22).
 *
 * It says "if your number needs verifying" rather than "we sent a code",
 * because a resend for a number nobody holds must be indistinguishable from a
 * resend for one that exists. A confident sentence here would turn this button
 * into a way to ask which phone numbers have accounts.
 */
export const RESEND_SENT =
  "If your number needs verifying, we sent a new code.";

export const NOT_CONFIGURED =
  "The app backend is not configured yet (missing environment variables).";

export function authCopyFor(failure: Failure): string {
  switch (failure.code) {
    case "phone_taken":
      return "An account with this phone number already exists.";
    case "credentials_incorrect":
      // The **phone** wording, which is right for the consumer and staff
      // screens. The admin screen says "Email or password" and overrides this
      // itself, because it is the one surface where an email is the identifier.
      return "Phone or password is incorrect.";
    case "account_incomplete":
      return "Something went wrong with your account. Please sign up again.";
    case "wrong_surface":
      // H57: the number and password are right, for the clinic login.
      return "That is a staff login. Use the business and staff login instead.";
    case "otp_expired":
      return "This code has expired. Request a new one.";
    case "otp_already_used":
      return "This code was already used. Log in below.";
    case "otp_attempts_exhausted":
      return "Too many attempts. Request a new code.";
    case "otp_resend_cooldown":
      return "Please wait a moment before requesting another code.";
    case "rate_limited":
      return "Too many attempts. Please try again shortly.";
    case "not_configured":
      return NOT_CONFIGURED;
    default:
      return SYSTEM_ERROR;
  }
}

/**
 * Coded failure → the FormState these screens render.
 *
 * `otp_incorrect` is the one refusal that is a **banner code rendered as a
 * field**: it is not a malformed input (the code was the right shape), but the
 * only useful place to say it is under the box they just typed into.
 */
export function formStateFor(
  failure: Failure,
  values: Record<string, string>,
): FormState {
  if (failure.fieldErrors) {
    return { fieldErrors: renderFieldErrors(failure.fieldErrors), values };
  }
  if (failure.code === "otp_incorrect") {
    return { fieldErrors: { code: "That code isn't right." }, values };
  }
  return { formError: authCopyFor(failure), values };
}

/**
 * Coded failure -> the FormState the **staff password change** renders (H44).
 *
 * Lifted out of `./staffPassword.ts` in R22 T11 for the same reason the rest of
 * this file was lifted out of `./auth.ts`: a `"use server"` file can export
 * nothing but async functions, so these sentences could be neither shared nor
 * pinned by a test. The static web app needs the same three answers.
 *
 * **Both refusals render under a field rather than in a banner**, and which
 * field is the whole point: one names the box they mistyped, the other names
 * the box they have to change. A banner would make the reader hunt for it.
 */
export function passwordChangeStateFor(
  failure: Failure,
  values: Record<string, string> = {},
): FormState {
  if (failure.fieldErrors) {
    return { fieldErrors: renderFieldErrors(failure.fieldErrors), values };
  }
  switch (failure.code) {
    case "current_password_incorrect":
      return {
        fieldErrors: { currentPassword: "Current password is incorrect." },
        values,
      };
    case "password_unchanged":
      return {
        fieldErrors: {
          newPassword: "Choose a password different from your current one.",
        },
        values,
      };
    case "not_configured":
      return { formError: NOT_CONFIGURED, values };
    default:
      return { formError: SYSTEM_ERROR, values };
  }
}
